/* 707dot homepage motion and interaction enhancements. */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const mobileHeaderViewport = window.matchMedia('(max-width: 768px)');
  const designMode = Boolean(window.Shopify && Shopify.designMode);

  const setupHorizontalRail = (rail) => {
    if (rail.dataset.sevenRailReady === 'true') return;
    rail.dataset.sevenRailReady = 'true';

    let active = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;
    let suppressClickUntil = 0;

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      active = true;
      dragged = false;
      startX = event.clientX;
      startScrollLeft = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', (event) => {
      if (!active) return;

      const distance = event.clientX - startX;
      if (Math.abs(distance) > 4 && !dragged) {
        dragged = true;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      }

      if (!dragged) return;
      event.preventDefault();
      rail.scrollLeft = startScrollLeft - distance;
    });

    const endDrag = (event) => {
      if (!active) return;

      active = false;
      rail.classList.remove('is-dragging');
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      if (dragged) suppressClickUntil = Date.now() + 240;
    };

    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);
    rail.addEventListener('lostpointercapture', () => {
      active = false;
      rail.classList.remove('is-dragging');
    });

    rail.addEventListener(
      'click',
      (event) => {
        if (Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      rail.scrollBy({
        left: rail.clientWidth * 0.72 * (event.key === 'ArrowRight' ? 1 : -1),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    });

    rail.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
  };

  const setupRails = (scope = document) => {
    scope.querySelectorAll('.motion-horizontal-rail').forEach(setupHorizontalRail);
  };

  const releaseOrphanedIntroState = () => {
    if (!document.documentElement.classList.contains('intro-sequence-pending')) return;
    if (document.querySelector('[data-intro-overlay]')) return;

    document.documentElement.classList.remove('intro-boot-pending', 'intro-sequence-pending', 'intro-using-header-logo', 'intro-logo-travelling');
    document.documentElement.classList.add('intro-sequence-complete');
    document.querySelector('.section-header')?.classList.add('header-ready');
  };

  const setupHomepageMotion = (scope = document) => {
    if (!document.body.classList.contains('template-index')) return;

    const revealGroups = [
      ['.motion-centered-logo', 'media'],
      ['.image-with-text__media', 'media'],
      ['.image-with-text__content > *', 'text'],
      ['.motion-product-card', 'card']
    ];

    const sectionOrders = new Map();
    const revealTargets = new Map();
    const observer =
      !reduceMotion && !designMode && 'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries, instance) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                (revealTargets.get(entry.target) || []).forEach((element) => {
                  element.classList.add('is-seven-motion-visible');
                });
                revealTargets.delete(entry.target);
                instance.unobserve(entry.target);
              });
            },
            { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
          )
        : null;

    revealGroups.forEach(([selector, kind]) => {
      scope.querySelectorAll(selector).forEach((element) => {
        if (element.classList.contains('seven-motion-reveal') || element.classList.contains('scroll-trigger')) return;
        if (element.closest('.video-banner-with-intro')) return;

        const section = element.closest('.shopify-section') || element.parentElement;
        const order = sectionOrders.get(section) || 0;
        sectionOrders.set(section, order + 1);

        element.classList.add('seven-motion-reveal', `seven-motion-reveal--${kind}`);
        element.style.setProperty('--seven-motion-delay', `${Math.min(order, 6) * 85}ms`);

        if (observer) {
          // Fully clipped text has no intersection area. Observe its unclipped
          // content container so the reveal can start when it enters the viewport.
          const trigger = kind === 'text' ? element.parentElement : element;
          const targets = revealTargets.get(trigger) || [];
          targets.push(element);
          revealTargets.set(trigger, targets);
          observer.observe(trigger);
        } else {
          element.classList.add('is-seven-motion-visible');
        }
      });
    });
  };

  const setupHeaderParallax = () => {
    if (!document.body.classList.contains('template-index')) return;

    const sectionHeader = document.querySelector('.section-header');
    const header = sectionHeader?.querySelector('.header');
    if (!header || header.dataset.sevenParallaxReady === 'true') return;
    header.dataset.sevenParallaxReady = 'true';
    sectionHeader.classList.remove('header-motion-enabled');

    let pointerX = 0;
    let pointerY = 0;
    let scrollY = 0;
    let frame = 0;
    let enableTimer = 0;

    const parallaxEnabled = () => !reduceMotion && !mobileHeaderViewport.matches;
    const currentScrollOffset = () => (parallaxEnabled() ? Math.max(-6, -window.scrollY * 0.012) : 0);

    const render = () => {
      frame = 0;
      header.style.setProperty('--header-parallax-x', `${pointerX.toFixed(2)}px`);
      header.style.setProperty('--header-parallax-y', `${(pointerY + scrollY).toFixed(2)}px`);
    };

    const requestRender = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };

    const scheduleMotionEnable = () => {
      window.clearTimeout(enableTimer);
      sectionHeader.classList.remove('header-motion-enabled');
      scrollY = currentScrollOffset();
      requestRender();

      enableTimer = window.setTimeout(() => {
        scrollY = currentScrollOffset();
        render();
        requestAnimationFrame(() => sectionHeader.classList.add('header-motion-enabled'));
      }, 1000);
    };

    if (!reduceMotion && finePointer) {
      window.addEventListener(
        'pointermove',
        (event) => {
          if (!parallaxEnabled()) {
            pointerX = 0;
            pointerY = 0;
            requestRender();
            return;
          }

          pointerX = (event.clientX / window.innerWidth - 0.5) * 7;
          pointerY = (event.clientY / window.innerHeight - 0.5) * 3.5;
          requestRender();
        },
        { passive: true }
      );

      document.documentElement.addEventListener('mouseleave', () => {
        pointerX = 0;
        pointerY = 0;
        requestRender();
      });
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!parallaxEnabled()) {
          scrollY = 0;
          requestRender();
          return;
        }

        scrollY = currentScrollOffset();
        requestRender();
      },
      { passive: true }
    );

    const handleHeaderViewportChange = () => {
      if (!parallaxEnabled()) {
        pointerX = 0;
        pointerY = 0;
      }
      scrollY = currentScrollOffset();
      requestRender();
    };

    if (mobileHeaderViewport.addEventListener) {
      mobileHeaderViewport.addEventListener('change', handleHeaderViewportChange);
    } else {
      mobileHeaderViewport.addListener(handleHeaderViewportChange);
    }

    document.addEventListener('sevenzeroseven:intro-complete', scheduleMotionEnable);

    if (document.documentElement.classList.contains('intro-sequence-complete')) {
      scheduleMotionEnable();
    }
  };

  const initialize = (scope = document) => {
    releaseOrphanedIntroState();
    setupRails(scope);
    setupHomepageMotion(scope);
    setupHeaderParallax();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  // Last-resort safety: animation failures must never leave navigation hidden.
  window.setTimeout(() => {
    if (!document.documentElement.classList.contains('intro-sequence-pending')) return;

    document.documentElement.classList.remove('intro-boot-pending', 'intro-sequence-pending', 'intro-using-header-logo', 'intro-logo-travelling');
    document.documentElement.classList.add('intro-sequence-complete');
    document.querySelector('.section-header')?.classList.add('header-ready');
    document.querySelector('[data-intro-overlay]')?.remove();
    document.dispatchEvent(new CustomEvent('sevenzeroseven:intro-complete'));
  }, 12500);
})();
